// HKDSE 2020 Paper 1 Knowledge Graph Samples
// Organized by question types with systematic naming

export const hkdse2020Samples = {
  algebra: [
    {
      id: 'hkdse2020_algebra_01',
      problem: "Simplify $\\frac{(mn^{-2})^5}{m^{-4}}$ and express the answer in positive index form.",
      problemType: "algebra_indices",
      difficulty: "intermediate",
      description: "Simplifying expressions with negative indices",
      graphData: {
        nodes: [
          {
            id: "negative_indices",
            name: "Negative Indices",
            type: "concepts",
            description: "Understanding negative indices and their properties",
            value: "a⁻ⁿ = 1/aⁿ",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "power_of_power",
            name: "Power of Power",
            type: "concepts",
            description: "When raising a power to another power, multiply the indices",
            value: "(aᵐ)ⁿ = aᵐⁿ",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "division_of_powers",
            name: "Division of Powers",
            type: "concepts",
            description: "When dividing powers with the same base, subtract the indices",
            value: "aᵐ ÷ aⁿ = aᵐ⁻ⁿ",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_simplify_numerator",
            name: "Step 1: Simplify Numerator",
            type: "steps",
            description: "Apply power of power rule to (mn⁻²)⁵",
            value: "(mn⁻²)⁵ = m⁵n⁻¹⁰",
            importance: 8,
            x: 0, y: 0, z: 25
          },
          {
            id: "step2_divide",
            name: "Step 2: Divide by Denominator",
            type: "steps",
            description: "Divide m⁵n⁻¹⁰ by m⁻⁴",
            value: "m⁵n⁻¹⁰ ÷ m⁻⁴ = m⁹n⁻¹⁰",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "step3_convert_negative",
            name: "Step 3: Convert to Positive Indices",
            type: "steps",
            description: "Convert negative indices to positive using reciprocal",
            value: "m⁹n⁻¹⁰ = m⁹/n¹⁰",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "The simplified expression in positive indices",
            value: "m⁹/n¹⁰",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "negative_indices", to: "step3_convert_negative", type: "enables", strength: 10 },
          { from: "power_of_power", to: "step1_simplify_numerator", type: "enables", strength: 9 },
          { from: "division_of_powers", to: "step2_divide", type: "enables", strength: 9 },
          { from: "step1_simplify_numerator", to: "step2_divide", type: "leads to", strength: 10 },
          { from: "step2_divide", to: "step3_convert_negative", type: "leads to", strength: 10 },
          { from: "step3_convert_negative", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'hkdse2020_algebra_02',
      problem: "Factorize: (a) $a^2 + a - 6$ (b) $a^4 + a^3 - 6a^2$",
      problemType: "factorization",
      difficulty: "intermediate",
      description: "Factorization of polynomial expressions",
      graphData: {
        nodes: [
          {
            id: "quadratic_factorization",
            name: "Quadratic Factorization",
            type: "concepts",
            description: "Factoring quadratic expressions",
            value: "ax² + bx + c = (px + q)(rx + s)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "common_factor",
            name: "Common Factor",
            type: "concepts",
            description: "Finding the greatest common factor",
            value: "GCF of terms",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "step1_part_a",
            name: "Step 1: Part (a) - Factor Quadratic",
            type: "steps",
            description: "Factor a² + a - 6",
            value: "(a + 3)(a - 2)",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "step2_part_b",
            name: "Step 2: Part (b) - Find Common Factor",
            type: "steps",
            description: "Find GCF of a⁴, a³, -6a²",
            value: "GCF = a²",
            importance: 8,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_part_b",
            name: "Step 3: Part (b) - Factor Out",
            type: "steps",
            description: "Factor out a² and use part (a) result",
            value: "a²(a² + a - 6) = a²(a + 3)(a - 2)",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "Fully factorized expressions",
            value: "(a) (a + 3)(a - 2), (b) a²(a + 3)(a - 2)",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "quadratic_factorization", to: "step1_part_a", type: "enables", strength: 10 },
          { from: "common_factor", to: "step2_part_b", type: "enables", strength: 9 },
          { from: "step1_part_a", to: "step3_part_b", type: "enables", strength: 8 },
          { from: "step2_part_b", to: "step3_part_b", type: "leads to", strength: 10 },
          { from: "step3_part_b", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  ratios: [
    {
      id: 'hkdse2020_ratios_01',
      problem: "Let $a$, $b$ and $c$ be non-zero numbers such that $\\frac{a}{b} = \\frac{6}{7}$ and $3a = 4c$. Find $\\frac{b + 2c}{a + 2b}$.",
      problemType: "ratios_proportions",
      difficulty: "intermediate",
      description: "Working with ratios and proportions",
      graphData: {
        nodes: [
          {
            id: "ratio_relationship",
            name: "Ratio Relationship",
            type: "concepts",
            description: "Understanding ratio relationships",
            value: "a/b = 6/7",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "proportion_relationship",
            name: "Proportion Relationship",
            type: "concepts",
            description: "Understanding proportion relationships",
            value: "3a = 4c",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "step1_express_in_terms",
            name: "Step 1: Express in Terms",
            type: "steps",
            description: "Express a and c in terms of b",
            value: "a = 6b/7, c = 3a/4 = 18b/28 = 9b/14",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step2_substitute",
            name: "Step 2: Substitute",
            type: "steps",
            description: "Substitute into the required expression",
            value: "(b + 2c)/(a + 2b) = (b + 18b/14)/(6b/7 + 2b)",
            importance: 8,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_simplify",
            name: "Step 3: Simplify",
            type: "steps",
            description: "Simplify the expression",
            value: "(32b/14)/(20b/7) = (16b/7)/(20b/7) = 16/20 = 4/5",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "The simplified ratio",
            value: "4/5",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "ratio_relationship", to: "step1_express_in_terms", type: "enables", strength: 10 },
          { from: "proportion_relationship", to: "step1_express_in_terms", type: "enables", strength: 9 },
          { from: "step1_express_in_terms", to: "step2_substitute", type: "leads to", strength: 9 },
          { from: "step2_substitute", to: "step3_simplify", type: "leads to", strength: 10 },
          { from: "step3_simplify", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    }
  ]
}; 